import { ArrowRightOutlined, RocketOutlined } from "@ark/ui/icons";
import { createStyles } from "@ark/ui/styling";
import { Button, Dialog, Tag, Title } from "@ark/ui/controls";
import { useEffect, useState } from "react";

import {
  activateMajorReleasePrompt,
  markLatestMajorReleaseSeen,
  releaseAnchor,
  setMajorReleasePromptActive,
  setMajorReleasePromptPending,
  type ProductRelease,
  unseenMajorRelease,
} from "./productReleases";

const useStyles = createStyles(({ css, token }) => ({
  modal: css`
    max-height: calc(100dvh - 24px);
    overflow: auto;
    .ark-dialog-header,
    .ark-dialog-footer {
      flex: none;
    }

    .ark-dialog-body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    @media (max-width: ${token.screenXSMax}px), (max-height: 620px) {
      .ark-dialog-body {
        padding: 16px;
      }

      .ark-dialog-footer {
        display: grid;
        gap: 8px;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        margin-top: 12px;

        .ark-button {
          margin-inline-start: 0;
        }
      }
    }
  `,
  banner: css`
    background:
      radial-gradient(
        circle at 100% 0,
        ${token.colorPrimaryBgHover},
        transparent 48%
      ),
      ${token.colorFillQuaternary};
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: 14px;
    margin: 4px 0 18px;
    padding: 20px;

    @media (max-width: ${token.screenXSMax}px), (max-height: 620px) {
      margin-bottom: 12px;
      padding: 14px;
    }
  `,
  mark: css`
    align-items: center;
    background: ${token.colorPrimary};
    border-radius: 11px;
    color: ${token.colorTextLightSolid};
    display: flex;
    font-size: 22px;
    height: 44px;
    justify-content: center;
    margin-bottom: 16px;
    width: 44px;

    @media (max-width: ${token.screenXSMax}px), (max-height: 620px) {
      font-size: 18px;
      height: 36px;
      margin-bottom: 10px;
      width: 36px;
    }
  `,
  summary: css`
    color: ${token.colorTextSecondary};
    line-height: 1.65;
    margin: 8px 0 0;
    overflow-wrap: anywhere;
  `,
  highlights: css`
    display: grid;
    gap: 8px;
    margin: 0;
    padding-left: 20px;

    li::marker {
      color: ${token.colorPrimary};
    }

    li {
      overflow-wrap: anywhere;
    }

    @media (max-height: 620px) {
      gap: 5px;
    }
  `,
}));

export function MajorReleasePrompt({
  onNavigate,
  tenantId,
  userId,
}: {
  onNavigate: (path: string) => void;
  tenantId: string;
  userId: string;
}) {
  const { styles } = useStyles();
  const [pendingRelease] = useState<ProductRelease | undefined>(() =>
    unseenMajorRelease(tenantId, userId, safeLocalStorage()),
  );
  const [release, setRelease] = useState<ProductRelease | undefined>();

  useEffect(() => {
    setMajorReleasePromptPending();
    const controller = new AbortController();
    if (!pendingRelease) {
      setMajorReleasePromptActive(false);
      return () => controller.abort();
    }
    void activateMajorReleasePrompt(controller.signal).then((active) => {
      if (!active || controller.signal.aborted) return;
      markLatestMajorReleaseSeen(tenantId, userId, safeLocalStorage());
      setRelease(pendingRelease);
    });
    return () => {
      controller.abort();
      setMajorReleasePromptActive(false);
    };
  }, [pendingRelease, tenantId, userId]);

  const settle = (navigate: boolean) => {
    if (!release) return;
    markLatestMajorReleaseSeen(tenantId, userId, safeLocalStorage());
    setRelease(undefined);
    setMajorReleasePromptActive(false);
    if (navigate) onNavigate(`/changelog#${releaseAnchor(release)}`);
  };

  return (
    <Dialog
      closeOnBackdrop={false}
      onClose={() => settle(false)}
      open={Boolean(release)}
      className={styles.modal}
      title="Ark 有重大更新"
      width={520}
      footer={
        <>
          <Button onClick={() => settle(false)}>稍后查看</Button>
          <Button
            icon={<ArrowRightOutlined />}
            onClick={() => settle(true)}
            type="primary"
          >
            查看更新
          </Button>
        </>
      }
    >
      {release ? (
        <>
          <div className={styles.banner}>
            <div className={styles.mark}>
              <RocketOutlined />
            </div>
            <Tag color="blue">Ark {release.version}</Tag>
            <Title level={3} style={{ margin: "10px 0 0" }}>
              {release.title}
            </Title>
            <p className={styles.summary}>{release.summary}</p>
          </div>
          <ul className={styles.highlights}>
            {release.highlights.slice(0, 3).map((highlight) => (
              <li key={highlight.title}>{highlight.title}</li>
            ))}
          </ul>
        </>
      ) : null}
    </Dialog>
  );
}

function safeLocalStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
